import { useEffect, useId, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'

export type SelectOption = { value: string; label: string }

type SelectProps = {
  label: string
  value: string
  onChange: (value: string) => void
  options: readonly (SelectOption | string)[]
  /** Shown when value is '' — the "any" state of a filter. */
  placeholder?: string
  /** Keeps the label for the screen reader but takes it off the page, for a
      toolbar where the chosen value already says what the control is. */
  hideLabel?: boolean
  className?: string
}

const normalise = (option: SelectOption | string): SelectOption =>
  typeof option === 'string' ? { value: option, label: option } : option

/* A listbox rather than a native <select>, because the native popup cannot be
   styled to match the rest of the site on any platform. Everything the native
   control gives away for free is re-implemented here: keyboard navigation,
   Escape, click-outside, and the ARIA that makes it announce correctly. */
function Select({
  label,
  value,
  onChange,
  options,
  placeholder = 'Any',
  hideLabel = false,
  className = '',
}: SelectProps) {
  const items = options.map(normalise)
  const selectedIndex = items.findIndex((item) => item.value === value)

  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(Math.max(selectedIndex, 0))

  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const id = useId()
  const listId = `${id}-list`
  const labelId = `${id}-label`

  const selected = selectedIndex >= 0 ? items[selectedIndex] : null

  /* Moving focus into the list is what makes the arrow keys work. */
  useEffect(() => {
    if (open) listRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  const openList = () => {
    setActiveIndex(Math.max(selectedIndex, 0))
    setOpen(true)
  }

  const close = (returnFocus = true) => {
    setOpen(false)
    if (returnFocus) buttonRef.current?.focus()
  }

  const commit = (index: number) => {
    onChange(items[index].value)
    close()
  }

  const onListKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, items.length - 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        break
      case 'Home':
        event.preventDefault()
        setActiveIndex(0)
        break
      case 'End':
        event.preventDefault()
        setActiveIndex(items.length - 1)
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        commit(activeIndex)
        break
      case 'Escape':
        event.preventDefault()
        close()
        break
      case 'Tab':
        close(false)
        break
    }
  }

  return (
    <div ref={rootRef} className={`relative flex flex-col gap-1 ${className}`}>
      <span
        id={labelId}
        className={hideLabel ? 'sr-only' : 'label-sm text-steel'}
      >
        {label}
      </span>

      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={`${labelId} ${id}-value`}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            openList()
          }
        }}
        className="flex w-full items-center justify-between gap-3 py-0.5 text-left text-[15px] font-medium text-platinum transition-colors hover:text-lime-deep"
      >
        <span id={`${id}-value`} className="truncate">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          aria-hidden="true"
          strokeWidth={1.7}
          className={`size-3.5 shrink-0 text-lime-deep transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={labelId}
          aria-activedescendant={`${id}-option-${activeIndex}`}
          onKeyDown={onListKeyDown}
          className="absolute top-full left-0 z-30 mt-3 max-h-72 w-full min-w-[13rem] overflow-y-auto rounded-2xl border border-line bg-elevated p-1.5 shadow-lift focus:outline-none"
        >
          {items.map((item, index) => {
            const isSelected = item.value === value
            return (
              <li
                key={item.value || 'any'}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={isSelected}
                onClick={() => commit(index)}
                onPointerEnter={() => setActiveIndex(index)}
                className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-[14px] transition-colors ${
                  index === activeIndex
                    ? 'bg-lime-ink text-platinum'
                    : 'text-silver'
                }`}
              >
                {item.label}
                {isSelected ? (
                  <Check
                    aria-hidden="true"
                    strokeWidth={2}
                    className="size-3.5 shrink-0 text-lime-deep"
                  />
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

export default Select
